import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useBackNav } from '../lib/backNav';
import { applyFramingHandoff, cameFromFraming, tagFramingSource, wantsFramingHandoff } from '../lib/framingHandoff';
import { bootIntent } from '../lib/share';
import { useStore } from '../store/store';
import { CartScreen } from './screens/CartScreen';
import { CheckoutScreen } from './screens/CheckoutScreen';
import { SizePaperScreen } from './screens/SizePaperScreen';
import { ErrorScreen, LoadingScreen, RestartScreen } from './screens/SystemScreens';
import { UploadScreen } from './screens/UploadScreen';
import { ShareBanner, ShareConfirmDialog, ShareFailedScreen } from './share/ShareGate';

/**
 * The single-page ordering flow at "/". Bootstraps the project (share / resume /
 * localStorage / create) on mount, then renders the screen for the current step.
 * ?share=<id>.<token> (a friend's shared photos) takes precedence over ?resume=.
 */
export function OrderingFlow() {
  const phase = useStore((s) => s.phase);
  const step = useStore((s) => s.step);
  const shareFlow = useStore((s) => s.shareFlow);
  const bootstrap = useStore((s) => s.bootstrap);
  const [params, setParams] = useSearchParams();
  // Browser Back (mobile edge-swipe incl.) steps back WITHIN the flow.
  useBackNav();

  useEffect(() => {
    const intent = bootIntent(params); // share beats resume
    void bootstrap(
      intent.kind === 'resume' ? intent.param : null,
      intent.kind === 'share' ? intent.param : null,
    ).then(() => {
      // Opened from the framing landing page (with or without a photo): tag
      // the order so the weekly report can count framing-started orders.
      if (intent.kind === 'none' && cameFromFraming(params)) void tagFramingSource();
      if (intent.kind !== 'none') {
        // Consume the one-time params so a refresh uses localStorage.
        params.delete('resume');
        params.delete('share');
        setParams(params, { replace: true });
      } else if (wantsFramingHandoff(params)) {
        // Arrived from the framing page with a photo already tried on: upload it
        // and open Size & Paper with that frame picked. The stored record is
        // deleted on read, so a refresh cannot add the photo twice.
        params.delete('handoff');
        setParams(params, { replace: true });
        void applyFramingHandoff();
      }
    });
    // Run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (shareFlow?.state === 'failed') return <ShareFailedScreen flow={shareFlow} />;
  if (phase === 'idle' || phase === 'loading') return <LoadingScreen />;
  if (phase === 'expired') return <RestartScreen />;
  if (phase === 'error') return <ErrorScreen />;

  const screen = (() => {
    switch (step) {
      case 1:
        return <SizePaperScreen />;
      case 2:
        return <CartScreen />;
      case 3:
        return <CheckoutScreen />;
      default:
        return <UploadScreen />;
    }
  })();

  return (
    <>
      {screen}
      {step === 1 && <ShareBanner />}
      {shareFlow?.state === 'confirm' && <ShareConfirmDialog param={shareFlow.param} />}
    </>
  );
}
